import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { ChevronRight, ChevronLeft, Check, Loader2 } from 'lucide-react';

import { OnboardingStepPersonal } from './onboarding/OnboardingStepPersonal';
import { OnboardingStepCompany } from './onboarding/OnboardingStepCompany';
import { OnboardingStepLocations } from './onboarding/OnboardingStepLocations';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// eslint-disable-next-line no-unused-vars
const OnboardingModal = ({ isOpen, onClose, onComplete }) => {
  const { user, refreshUser } = useAuth();
  // eslint-disable-next-line no-unused-vars
  const { t } = useLanguage();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [prefillLoading, setPrefillLoading] = useState(true);

  const [personalInfo, setPersonalInfo] = useState({
    full_name: '', email: '', phone: '', phone_extension: '', mobile: '',
    preferred_contact_method: 'email', preferred_language: 'en', job_title: '',
  });

  const [companyInfo, setCompanyInfo] = useState({
    company_name: '', company_email: '', website: '', industry: '',
  });

  const [locations, setLocations] = useState([{
    name: 'Main Office', address: '', city: '', province: '',
    postal_code: '', country: 'Canada', phone: '', email: '', is_primary: true,
  }]);

  useEffect(() => {
    if (!isOpen) return;
    const fetchPrefillData = async () => {
      setPrefillLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/onboarding/prefill`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setPersonalInfo({
            full_name: data.personal?.full_name || user?.full_name || '',
            email: data.personal?.email || user?.email || '',
            phone: data.personal?.phone || '',
            phone_extension: data.personal?.phone_extension || '',
            mobile: data.personal?.mobile || '',
            preferred_contact_method: data.personal?.preferred_contact_method || 'email',
            preferred_language: data.personal?.preferred_language || 'en',
            job_title: data.personal?.job_title || '',
          });
          setCompanyInfo({
            company_name: data.company?.company_name || '',
            company_email: data.company?.company_email || '',
            website: data.company?.website || '',
            industry: data.company?.industry || '',
          });
          if (data.company?.locations?.length > 0) {
            setLocations(data.company.locations);
          }
        }
      } catch (error) {
        console.error('Error fetching prefill data:', error);
      } finally {
        setPrefillLoading(false);
      }
    };
    fetchPrefillData();
  }, [isOpen, user?.full_name, user?.email]);

  const addLocation = () => {
    setLocations([...locations, {
      name: '', address: '', city: '', province: '', postal_code: '',
      country: 'Canada', phone: '', email: '', is_primary: false,
    }]);
  };

  const removeLocation = (index) => {
    if (locations.length <= 1) return;
    const newLocations = locations.filter((_, i) => i !== index);
    if (locations[index].is_primary && newLocations.length > 0) {
      newLocations[0].is_primary = true;
    }
    setLocations(newLocations);
  };

  const updateLocation = (index, field, value) => {
    const newLocations = [...locations];
    newLocations[index][field] = value;
    if (field === 'is_primary' && value) {
      newLocations.forEach((loc, i) => { if (i !== index) loc.is_primary = false; });
    }
    setLocations(newLocations);
  };

  const handleSubmit = async () => {
    if (!personalInfo.full_name.trim()) { toast.error('Please enter your full name'); setStep(1); return; }
    if (!companyInfo.company_name.trim()) { toast.error('Please enter your company name'); setStep(2); return; }
    if (locations.length === 0 || !locations[0].name.trim()) {
      toast.error('Please add at least one location'); setStep(3); return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/onboarding/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ personal: personalInfo, company: { ...companyInfo, locations } }),
      });
      if (response.ok) {
        toast.success('Profile setup complete!');
        if (refreshUser) await refreshUser();
        onComplete?.();
        onClose();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save profile');
      }
    } catch (error) {
      console.error('Onboarding error:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-6">
      {[1, 2, 3].map((s) => (
        <div key={s} className="flex items-center">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
              s === step
                ? 'bg-[#7B1F2A] text-white'
                : s < step
                  ? 'bg-green-500 text-white'
                  : 'bg-slate-200 text-slate-500'
            }`}
          >
            {s < step ? <Check className="h-4 w-4" /> : s}
          </div>
          {s < 3 && (
            <div className={`w-12 h-1 mx-1 ${s < step ? 'bg-green-500' : 'bg-slate-200'}`} />
          )}
        </div>
      ))}
    </div>
  );

  if (prefillLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={() => {}}>
        <DialogContent className="bg-white max-w-2xl">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#7B1F2A]" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="onboarding-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-slate-900">
            Welcome! Let's set up your profile
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            Please provide your information to complete your account setup. This helps us serve you better.
          </DialogDescription>
        </DialogHeader>

        {renderStepIndicator()}

        <div className="py-4">
          {step === 1 && <OnboardingStepPersonal personalInfo={personalInfo} setPersonalInfo={setPersonalInfo} />}
          {step === 2 && <OnboardingStepCompany companyInfo={companyInfo} setCompanyInfo={setCompanyInfo} />}
          {step === 3 && (
            <OnboardingStepLocations
              locations={locations}
              companyInfo={companyInfo}
              addLocation={addLocation}
              removeLocation={removeLocation}
              updateLocation={updateLocation}
            />
          )}
        </div>

        <div className="flex justify-between pt-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={() => setStep(Math.max(1, step - 1))}
            disabled={step === 1 || loading}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>

          {step < 3 ? (
            <Button
              type="button"
              onClick={() => setStep(step + 1)}
              className="bg-[#7B1F2A] hover:bg-[#9B2A38] text-white"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="onboard-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Complete Setup
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingModal;
